import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { ACTIVE_EMPLOYEE_STATUSES, getEmployees } from '@/lib/employees';
import { useEmployeeNameFormatter } from '@/lib/employee-name';
import {
  buildReportReassignments,
  managerOptionsFor,
  type ReportChoices,
  type ReportReassignment,
  type UnsettledDirectReport,
} from '../lib/deactivation-reports';

/**
 * `SearchableSelect` shows the placeholder for an empty value, so "" cannot
 * stand for a deliberate "nobody" — it would look identical to an untouched
 * row. A sentinel option carries that choice instead.
 */
const NO_MANAGER = '__no_manager__';

/**
 * One request for the whole roster, filtered inside the picker. The endpoint
 * assembles its query by hand and never applies `MAX_LIMIT`, so a large limit
 * comes back in full — measured. A server-searched picker would be the better
 * shape, but it needs a change to the shared `SearchableSelect`, which is out of
 * this ticket's scope; the notice below owns the consequence honestly.
 */
const CANDIDATE_LIMIT = 1000;

interface ReassignReportsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The employee being deactivated — never offered as a new manager. */
  employeeId: string;
  employeeName: string;
  /** Reports the backend refused to orphan. */
  reports: UnsettledDirectReport[];
  onConfirm: (assignments: ReportReassignment[]) => void;
  isSubmitting?: boolean;
}

export function ReassignReportsDialog({
  open,
  onOpenChange,
  employeeId,
  employeeName,
  reports,
  onConfirm,
  isSubmitting = false,
}: ReassignReportsDialogProps) {
  const formatName = useEmployeeNameFormatter();
  const [choices, setChoices] = useState<ReportChoices>({});
  // Display only: the blanket answer last applied. Nothing reads it to decide an
  // assignment — the blanket picker writes into `choices` instead, so an answer
  // cannot quietly disappear when the list of reports changes.
  const [lastBlanket, setLastBlanket] = useState('');

  // The backend can answer a retry with a different set of people. Answers for
  // those still on the list survive; anything else is dropped.
  const signature = reports.map((r) => r.id).join('|');
  const [answeredFor, setAnsweredFor] = useState(signature);
  if (answeredFor !== signature) {
    setAnsweredFor(signature);
    const stillListed = new Set(reports.map((r) => r.id));
    setChoices((prev) =>
      Object.fromEntries(Object.entries(prev).filter(([id]) => stillListed.has(id)))
    );
    setLastBlanket('');
  }

  const { data, isLoading, isError } = useQuery({
    queryKey: ['employees', 'reassign-candidates'],
    enabled: open,
    staleTime: 60_000,
    queryFn: async () => {
      const response = await getEmployees({
        page: 1,
        limit: CANDIDATE_LIMIT,
        statuses: [...ACTIVE_EMPLOYEE_STATUSES],
      });
      return {
        candidates: response.data,
        total: response.meta?.total ?? response.data.length,
      };
    },
  });

  const candidates = data?.candidates ?? [];
  const notListed = Math.max(0, (data?.total ?? 0) - candidates.length);

  /** A record can carry a blank name; a blank row would be invisible but clickable. */
  const labelFor = (person: {
    firstName: string;
    lastName: string;
    preferredName?: string | null;
    workEmail?: string;
  }) =>
    formatName({
      firstName: person.firstName,
      lastName: person.lastName,
      preferredName: person.preferredName,
    }) ||
    person.workEmail ||
    'Unnamed employee';

  // labelFor closes over the name formatter, which only changes when the
  // name-display setting does.
  const options = useMemo(
    () => candidates.map((c) => ({ id: c.id, name: labelFor(c), status: c.status })),
    [candidates, formatName]
  );

  const reportIds = useMemo(() => new Set(reports.map((r) => r.id)), [reports]);

  /** Per row: anyone but this person and the leaver — promoting a peer is fine. */
  const optionsFor = (reportId: string) => [
    ...managerOptionsFor(reportId, options, employeeId).filter(
      // Except a peer who has already been pointed at this very person: the two
      // would end up managing each other, which the backend refuses. Better not
      // to offer the move than to explain it afterwards.
      (candidate) => choices[candidate.id] !== reportId
    ),
    { id: NO_MANAGER, name: 'Leave without a manager' },
  ];

  /**
   * The blanket answer lands on every row, so a report cannot be it — that would
   * make one of them their own manager.
   */
  const blanketOptions = useMemo(
    () => [
      ...options.filter((o) => o.id !== employeeId && !reportIds.has(o.id)),
      { id: NO_MANAGER, name: 'Leave everyone without a manager' },
    ],
    [options, employeeId, reportIds]
  );

  const toChoice = (value: string) => (value === NO_MANAGER ? null : value);

  const answerRow = (reportId: string, value: string) => {
    setChoices((prev) => ({ ...prev, [reportId]: toChoice(value) }));
  };

  const answerEveryone = (value: string) => {
    const choice = toChoice(value);
    setChoices(Object.fromEntries(reports.map((r) => [r.id, choice])));
    setLastBlanket(value);
  };

  const valueFor = (reportId: string): string => {
    if (!Object.prototype.hasOwnProperty.call(choices, reportId)) return '';
    const choice = choices[reportId];
    return choice === null ? NO_MANAGER : choice;
  };

  const assignments = buildReportReassignments(reports, choices);
  const pickersDisabled = isLoading || isSubmitting || isError;

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setChoices({});
      setLastBlanket('');
    }
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Reassign direct reports</DialogTitle>
          <DialogDescription>
            {employeeName} still has {reports.length}{' '}
            {reports.length === 1 ? 'direct report' : 'direct reports'}. Choose who takes over
            before deactivating, or confirm they should be left without a manager.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {isError && (
            <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              Could not load the list of employees, so there is nobody to choose from. Close this
              and try again — deactivating now would leave the team without a manager.
            </p>
          )}

          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="shrink-0 text-sm text-muted-foreground">Reassign everyone to</span>
            <SearchableSelect
              value={lastBlanket}
              onChange={answerEveryone}
              options={blanketOptions}
              allowNone={false}
              disabled={pickersDisabled}
              placeholder={isLoading ? 'Loading employees…' : 'Select a manager'}
              searchPlaceholder="Search employees…"
              emptyText={isError ? 'Could not load employees.' : 'No employees found.'}
              ariaLabel="Reassign every direct report to"
              modal
              className="max-w-xs"
            />
            <p className="w-full text-xs text-muted-foreground">
              Applies to every row below, replacing any individual choice.
            </p>
          </div>

          <ul className="max-h-72 divide-y overflow-y-auto rounded-md border">
            {reports.map((report) => (
              <li key={report.id} className="flex items-center gap-3 p-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{labelFor(report)}</p>
                  {report.positionTitle && (
                    <p className="truncate text-xs text-muted-foreground">{report.positionTitle}</p>
                  )}
                </div>
                <SearchableSelect
                  value={valueFor(report.id)}
                  onChange={(value) => answerRow(report.id, value)}
                  options={optionsFor(report.id)}
                  allowNone={false}
                  disabled={pickersDisabled}
                  placeholder={isLoading ? 'Loading…' : 'Select a manager'}
                  searchPlaceholder="Search employees…"
                  emptyText={isError ? 'Could not load employees.' : 'No employees found.'}
                  ariaLabel={`New manager for ${labelFor(report)}`}
                  modal
                  className="w-56 shrink-0"
                />
              </li>
            ))}
          </ul>

          {notListed > 0 && (
            <p className="text-xs text-muted-foreground">
              Showing the first {candidates.length} employees; {notListed} more are not in the
              list. The search inside the picker only filters the ones loaded here.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={!assignments || isSubmitting || isError}
            onClick={() => assignments && onConfirm(assignments)}
          >
            {isSubmitting ? 'Deactivating…' : 'Reassign and deactivate'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
